// src/components/admin/HelpSupport.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import { collection, addDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  FiHelpCircle, FiMail, FiSend, FiChevronDown, FiChevronRight,
  FiAlertCircle, FiCheckCircle, FiClock, FiUser, FiCalendar
} from 'react-icons/fi';
import toast from 'react-hot-toast';

const HelpSupport = () => {
  const { currentUser, userProfile, hasPermission, isFirebaseAvailable } = useAuth();
  const { currentTenant } = useTenant();
  const [activeTab, setActiveTab] = useState('faq');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    description: ''
  });
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  // FAQ Data
  const faqData = [
    {
      id: 1,
      question: 'How do I reset my password?',
      answer: 'You can reset your password by clicking the "Forgot Password" link on the login screen, or by going to your profile settings and selecting "Change Password".'
    },
    {
      id: 2,
      question: 'How do I add a new client?',
      answer: 'Navigate to the Clients page and click the "Add Client" button. Fill in the required information and save. The client will be immediately available for appointment booking.'
    },
    {
      id: 3,
      question: 'How do I create an appointment?',
      answer: 'Go to the Calendar page and click on any time slot. Select the client, treatment type, and duration. The appointment will be saved automatically.'
    },
    {
      id: 4,
      question: 'How do I generate an invoice?',
      answer: 'Open an appointment from the calendar, and if you\'re an admin, you\'ll see a "Create Invoice" button. This will pre-fill invoice details from the appointment.'
    },
    {
      id: 5,
      question: 'How do I view my payslips?',
      answer: 'Go to your profile (click your name in the sidebar) and navigate to the Payroll tab. All your released payslips will be available there.'
    },
    {
      id: 6,
      question: 'What data protection measures are in place?',
      answer: 'HealeHub complies with GDPR and PDPA regulations. All data is encrypted in transit and at rest, stored in secure data centers, and access is strictly controlled by role-based permissions.'
    },
    {
      id: 7,
      question: 'How do I export my data?',
      answer: 'Data export requests can be submitted through the support ticket system. As per GDPR/PDPA requirements, we will provide your data in a portable format within 30 days.'
    },
    {
      id: 8,
      question: 'How do I delete my account?',
      answer: 'Account deletion requests must be submitted through the support ticket system. Please note that this action is irreversible and will permanently delete all your personal data.'
    },
    {
      id: 9,
      question: 'What are Advanced Compensation Plans?',
      answer: 'Advanced Compensation Plans replace simple role-based pay rates with sophisticated individual compensation structures. You can create salary, hourly, commission, or hybrid plans and assign them to specific staff members for automatic payroll calculations.'
    },
    {
      id: 10,
      question: 'How do I set up Compensation Plans?',
      answer: 'Go to Admin Panel → Settings → Compensation Plans. Toggle "Enable Advanced Compensation Plans", create your first plan using the wizard, then assign it to individual staff members. Each person can have their own unique compensation structure.'
    },
    {
      id: 11,
      question: 'Can I mix old role rates with new compensation plans?',
      answer: 'Yes! Compensation plans are assigned individually per staff member. Some staff can use the new advanced plans while others continue with their existing role rates. You can migrate staff gradually at your own pace.'
    },
    {
      id: 12,
      question: 'What types of compensation plans are available?',
      answer: 'There are 4 types: (1) Salary - fixed monthly amount, (2) Hourly - pay per hour worked, (3) Commission - percentage of revenue generated, (4) Hybrid - base salary plus commission bonus. Choose the type that best fits each staff member\'s role.'
    },
    {
      id: 13,
      question: 'How does commission tracking work?',
      answer: 'When you mark invoices as "paid", the system automatically credits the staff member who created the original appointment. Commission percentages are calculated based on their assigned compensation plan and added to their payroll.'
    },
    {
      id: 14,
      question: 'Can I change someone\'s compensation plan mid-month?',
      answer: 'Yes, you can change compensation plans anytime with effective dates. The system will calculate pro-rated amounts automatically, ensuring fair compensation for work done under each plan.'
    }
  ];

  // Load support tickets (tenant-scoped)
  const loadTickets = async () => {
    if (!currentUser || !currentTenant?.id) return;

    setLoading(true);
    try {
      // Load tenant-scoped support tickets
      const ticketList = await tenantDataService.getCollection('support_tickets');
      
      // Filter by user if not admin
      const filteredTickets = hasPermission('admin') 
        ? ticketList || []
        : (ticketList || []).filter(ticket => ticket.userId === currentUser.uid);
      
      // Sort by creation date (newest first)
      const sortedTickets = filteredTickets.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setTickets(sortedTickets);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Error loading support tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'tickets') {
      loadTickets();
    }
  }, [activeTab, currentUser, currentTenant]);

  // Submit support ticket (tenant-scoped)
  const handleSubmitTicket = async (e) => {
    e.preventDefault();

    if (!ticketForm.subject || !ticketForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!currentTenant?.id) {
      toast.error('No clinic selected');
      return;
    }

    const ticketData = {
      ...ticketForm,
      userId: currentUser?.uid || 'anonymous',
      userEmail: userProfile?.email || currentUser?.email || 'unknown',
      userName: userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : 'Unknown User',
      clinicName: currentTenant.name,
      status: 'open',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setLoading(true);
    try {
      // Save to tenant-scoped collection
      const ticketId = Date.now().toString();
      await tenantDataService.setDocument('support_tickets', ticketId, ticketData);

      // Send email notification (in a real app, this would be handled by a cloud function)
      console.log('Email notification would be sent to support:', ticketData);

      toast.success('Support ticket submitted successfully');
      setTicketForm({ subject: '', category: 'general', priority: 'medium', description: '' });
      await loadTickets();
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error('Error submitting support ticket');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderFAQ = () => (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Frequently Asked Questions</h3>
        <p className="text-gray-600">Find answers to common questions about using HealeHub.</p>
      </div>

      <div className="space-y-3">
        {faqData.map((faq) => (
          <div key={faq.id} className="bg-white border border-gray-200 rounded-lg">
            <button
              onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900">{faq.question}</span>
              {expandedFAQ === faq.id ? (
                <FiChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <FiChevronRight className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {expandedFAQ === faq.id && (
              <div className="px-4 pb-4">
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTicketForm = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Submit Support Ticket</h3>
        <p className="text-gray-600">
          Can't find what you're looking for? Submit a support ticket and our team will help you.
        </p>
      </div>

      <form onSubmit={handleSubmitTicket} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject *
          </label>
          <input
            type="text"
            value={ticketForm.subject}
            onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Brief description of your issue"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={ticketForm.category}
              onChange={(e) => setTicketForm(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="general">General Support</option>
              <option value="technical">Technical Issue</option>
              <option value="billing">Billing & Invoices</option>
              <option value="compensation">Compensation Plans</option>
              <option value="account">Account Management</option>
              <option value="data">Data Export/Deletion</option>
              <option value="feature">Feature Request</option>
              <option value="bug">Bug Report</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority
            </label>
            <select
              value={ticketForm.priority}
              onChange={(e) => setTicketForm(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            value={ticketForm.description}
            onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Please provide detailed information about your issue..."
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <FiSend className="w-4 h-4" />
          <span>{loading ? 'Submitting...' : 'Submit Ticket'}</span>
        </button>
      </form>
    </div>
  );

  const renderTicketList = () => (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {hasPermission('admin') ? 'All Support Tickets' : 'My Support Tickets'}
        </h3>
        <p className="text-gray-600">
          {hasPermission('admin')
            ? 'Manage and respond to support tickets from all users.'
            : 'Track the status of your submitted support tickets.'
          }
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading tickets...</p>
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-8">
          <FiHelpCircle className="mx-auto w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Support Tickets</h3>
          <p className="text-gray-600">
            {hasPermission('admin')
              ? 'No support tickets have been submitted yet.'
              : 'You haven\'t submitted any support tickets yet.'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div key={ticket.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-gray-900">{ticket.subject}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority} priority
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{ticket.description}</p>

                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <FiUser className="w-3 h-3" />
                      <span>{ticket.userName}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <FiMail className="w-3 h-3" />
                      <span>{ticket.userEmail}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <FiCalendar className="w-3 h-3" />
                      <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="capitalize">{ticket.category}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Help & Support</h2>
        <p className="text-gray-600">Get help with HealeHub or submit a support request.</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('faq')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'faq'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            FAQ
          </button>
          <button
            onClick={() => setActiveTab('submit')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'submit'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            Submit Ticket
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'tickets'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            {hasPermission('admin') ? 'All Tickets' : 'My Tickets'}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'faq' && renderFAQ()}
        {activeTab === 'submit' && renderTicketForm()}
        {activeTab === 'tickets' && renderTicketList()}
      </div>

      {/* GDPR/PDPA Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FiAlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Data Protection Notice</h4>
            <p className="text-sm text-blue-800">
              Support tickets may contain personal information. All data is processed in accordance with
              GDPR and PDPA regulations. Tickets are retained for resolution purposes and deleted after
              2 years unless legally required to be retained longer.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSupport;
